import { Controller } from "@hotwired/stimulus"
import { GlobalWorkerOptions, TextLayer } from "pdfjs-dist/legacy/build/pdf.mjs"
import { loadPdfDocument } from "../pdf_document_loader.js"
import { rangeForPdfTextAnchor } from "../pdf_text_anchor.js"
import { normalizeSearchText } from "../search_text.js"

export default class extends Controller {
  static targets = ["candidate", "status", "submit"]
  static values = { documentUrl: String, workerUrl: String, labels: Object }

  async connect() {
    this.submitTarget.disabled = true
    this.abortController = new AbortController()
    GlobalWorkerOptions.workerSrc = this.workerUrlValue

    try {
      this.loadingTask = await loadPdfDocument({
        url: this.documentUrlValue,
        signal: this.abortController.signal,
        options: {
          enableScripting: false,
          isEvalSupported: false
        }
      })
      this.pdfDocument = await this.loadingTask.promise
      await Promise.all(this.candidateTargets.map((candidate) => this.resolveCandidate(candidate)))
      this.statusTarget.textContent = this.labelsValue.ready
      this.submitTarget.disabled = false
    } catch (error) {
      console.error("Unable to prepare PDF reading migration", error)
      this.statusTarget.textContent = this.labelsValue.error
    }
  }

  disconnect() {
    this.abortController?.abort()
    this.loadingTask?.destroy()
  }

  async resolveCandidate(candidate) {
    const result = candidate.dataset.percentage
      ? await this.resolvePercentage(Number(candidate.dataset.percentage))
      : await this.resolveSemantic(candidate)
    this.writeResult(candidate, result)
  }

  async resolveSemantic(candidate) {
    const source = JSON.parse(candidate.dataset.source)
    const rendered = await this.renderTextLayer(source.page)
    try {
      const range = rangeForPdfTextAnchor(rendered.layer, {
        preferredOffset: Number(candidate.dataset.startOffset),
        quote: candidate.dataset.quote,
        prefix: candidate.dataset.prefix,
        suffix: candidate.dataset.suffix
      })
      if (!range) throw Object.assign(new Error("Target PDF range is unavailable"), { code: "UNMAPPABLE_LOCATION" })

      const rects = this.normalizedRects(range, rendered.host)
      const location = candidate.dataset.locationKind === "selection"
        ? { format: "pdf", page: source.page, rects }
        : { format: "pdf", page: source.page, y: rects[0]?.y || 0 }
      return { location, semanticLocator: null }
    } finally {
      rendered.host.remove()
    }
  }

  async resolvePercentage(percentage) {
    const raw = percentage / 100 * this.pdfDocument.numPages
    const page = Math.min(this.pdfDocument.numPages, Math.floor(raw) + 1)
    const y = percentage >= 100 ? 1 : Math.max(0, Math.min(1, raw - Math.floor(raw)))
    const pdfPage = await this.pdfDocument.getPage(page)
    const text = (await pdfPage.getTextContent()).items.map((item) => item.str).join(" ")
    const quote = text.trim().slice(0, 200)
    const location = { format: "pdf", page, y }
    if (!quote) return { location, semanticLocator: null }

    const length = normalizeSearchText(quote).length

    return {
      location,
      semanticLocator: {
        format: "pdf",
        source: { page },
        block_index: 0,
        start_offset: 0,
        end_offset: length,
        quote,
        prefix: "",
        suffix: normalizeSearchText(text).slice(length, length + 80)
      }
    }
  }

  async renderTextLayer(pageNumber) {
    const page = await this.pdfDocument.getPage(pageNumber)
    const viewport = page.getViewport({ scale: 1 })
    const host = document.createElement("div")
    host.className = "pdf-migration-page"
    host.style.cssText = `position:fixed;left:-10000px;top:0;width:${viewport.width}px;height:${viewport.height}px`
    const layer = document.createElement("div")
    layer.className = "textLayer"
    host.append(layer)
    document.body.append(host)
    await new TextLayer({ textContentSource: page.streamTextContent(), container: layer, viewport }).render()
    return { host, layer }
  }

  normalizedRects(range, host) {
    const pageRect = host.getBoundingClientRect()
    return Array.from(range.getClientRects()).filter((rect) => rect.width > 0 && rect.height > 0).map((rect) => ({
      x: this.round((rect.left - pageRect.left) / pageRect.width),
      y: this.round((rect.top - pageRect.top) / pageRect.height),
      width: this.round(rect.width / pageRect.width),
      height: this.round(rect.height / pageRect.height)
    }))
  }

  writeResult(candidate, result) {
    const fieldPrefix = candidate.dataset.fieldPrefix
    const key = candidate.value
    const locationField = this.element.querySelector(`[data-migration-location='${CSS.escape(`${fieldPrefix}:${key}`)}']`)
    const semanticField = this.element.querySelector(`[data-migration-semantic-locator='${CSS.escape(`${fieldPrefix}:${key}`)}']`)
    locationField.value = JSON.stringify(result.location)
    if (semanticField && result.semanticLocator) semanticField.value = JSON.stringify(result.semanticLocator)
  }

  round(value) {
    return Math.round(value * 100_000) / 100_000
  }
}
